import React, { useMemo, useState } from 'react';
import { Alert, Button, Card, Descriptions, Space, Spin, Table, Tabs, Tag, Typography } from 'antd';
import { EditOutlined } from '@ant-design/icons';
import { Link, useNavigate, useParams } from 'react-router-dom';
import type { ColumnsType } from 'antd/es/table';
import { ApplicationCell, ExecutionCell, ExternalApplicationCell, PageLayout, StatusCell } from '../../ui';
import { EMPTY_FIELD, emptyListMessage, innSubtitleLine } from '../../ui/entityCard/entityCardLabels';
import useHasPermission from '../../hooks/useHasPermission';
import { useGetCarrierByIdQuery } from '../../store/api/referencesApi';
import CreateCarrierModal from '../../components/common/CreateCarrierModal';
import { EntityCardOverlayFrame } from '../../components/entityCard/EntityCardOverlayFrame';
import { useReferenceCardOverlay } from '../../hooks/useReferenceCardOverlay';
import {
  useCarrierPenalties,
  type CarrierRoutePenaltiesRow,
  type CarrierPenaltyTableRow,
  type CarrierApplicationRow,
  type CarrierTnRow
} from '../../hooks/useCarrierPenalties';
import { formatDate } from '../../utils/applicationDisplayFormat';
import { ENTITY_CARD_DASH } from '../../utils/entityCardRouteUtils';
const { Text } = Typography;

function formatDateTime(iso: string | undefined): string {
  if (!iso) return EMPTY_FIELD;
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return EMPTY_FIELD;
  return d.toLocaleString('ru-RU', {
    day: '2-digit',
    month: '2-digit',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit'
  });
}

function formatRub(value: number | null | undefined): string {
  if (value === null || value === undefined || Number.isNaN(Number(value))) {
    return ENTITY_CARD_DASH;
  }
  return `${Number(value).toLocaleString('ru-RU', {
    minimumFractionDigits: 0,
    maximumFractionDigits: 2
  })} ₽`;
}

/** Столбец «Штрафы»: количество и сумма. */
function formatPenaltySummaryLine(count: number, sumRub: number) {
  return (
    <span>
      {count} · {formatRub(sumRub)}
    </span>
  );
}

function penaltyStatusLabel(status: string): string {
  const s = String(status || '').toUpperCase();
  if (s === 'CALCULATED') return 'Рассчитан';
  if (s === 'APPLIED') return 'Применён к финансам';
  if (!s) return EMPTY_FIELD;
  return status;
}

function statusTagColor(status: string): string {
  const s = String(status || '').toUpperCase();
  if (s === 'APPLIED') return 'green';
  if (s === 'CALCULATED') return 'gold';
  return 'default';
}

const cardSurface: React.CSSProperties = {
  boxShadow: '0 1px 6px rgba(0, 0, 0, 0.06)',
  borderRadius: 8
};

const sectionTitle = (t: string) => <Typography.Title level={5} style={{ margin: 0 }}>{t}</Typography.Title>;

const CarrierCardPageContent: React.FC = () => {
  const navigate = useNavigate();
  const { isOverlay, close } = useReferenceCardOverlay();
  const { id: idParam } = useParams<{ id: string }>();
  const idNum = idParam ? parseInt(idParam, 10) : NaN;
  const skip = Number.isNaN(idNum);

  const canViewReferences = useHasPermission('references.view');
  const canEditReferences = useHasPermission('references.edit');
  const canCreateReferences = useHasPermission('references.create');
  const canModifyReferences = canEditReferences || canCreateReferences;

  const { data, isLoading, isError, error, refetch } = useGetCarrierByIdQuery(idNum, {
    skip: skip || !canViewReferences
  });

  const {
    routeRows,
    applicationRows,
    tnRows,
    totalRub,
    penaltyCount,
    loading: penaltiesLoading,
    error: penaltiesError,
    routesCount
  } = useCarrierPenalties(idNum, canViewReferences && !skip);

  const [editOpen, setEditOpen] = useState(false);
  const [expandedRowKeys, setExpandedRowKeys] = useState<readonly React.Key[]>([]);

  const routeTableColumns: ColumnsType<CarrierRoutePenaltiesRow> = useMemo(
    () => [
      {
        title: '№ маршрута',
        key: 'routeNum',
        width: 120,
        render: (_: unknown, r: CarrierRoutePenaltiesRow) => (
          <Link to={`/routes/${r.routeId}`} onClick={(e) => e.stopPropagation()} style={{ fontWeight: 600 }}>
            {r.routeNumber}
          </Link>
        )
      },
      {
        title: 'Маршрут',
        dataIndex: 'directionLabel',
        key: 'directionLabel',
        ellipsis: true,
        width: 220
      },
      {
        title: 'Клиент',
        key: 'clientName',
        width: 180,
        ellipsis: true,
        render: (_: unknown, r: CarrierRoutePenaltiesRow) => {
          if (r.clientId != null) {
            return (
              <Link to={`/clients/${r.clientId}`} onClick={(e) => e.stopPropagation()}>
                {r.clientName}
              </Link>
            );
          }
          return <Text type="secondary">{ENTITY_CARD_DASH}</Text>;
        }
      },
      {
        title: 'Исполнение',
        key: 'execution',
        width: 200,
        ellipsis: true,
        render: (_: unknown, r: CarrierRoutePenaltiesRow) => (
          <ExecutionCell
            route={{
              isCompanyRoute: r.isCompanyRoute,
              carrierContractRequestNumber: r.carrierContractRequestNumber,
              carrierContractRequestDate: r.carrierContractRequestDate
            }}
          />
        )
      },
      {
        title: 'Погрузка',
        dataIndex: 'loadingDateDisplay',
        key: 'loadingDateDisplay',
        width: 120
      },
      {
        title: 'Выгрузка',
        dataIndex: 'unloadingDateDisplay',
        key: 'unloadingDateDisplay',
        width: 120
      },
      {
        title: 'Статус',
        key: 'routeStatus',
        width: 130,
        render: (_: unknown, r: CarrierRoutePenaltiesRow) => <StatusCell status={r.routeStatus} />
      },
      {
        title: 'Штрафы',
        key: 'penaltySummary',
        width: 180,
        align: 'right',
        render: (_: unknown, r: CarrierRoutePenaltiesRow) => formatPenaltySummaryLine(r.penaltyCount, r.penaltySum)
      }
    ],
    []
  );

  const applicationColumns: ColumnsType<CarrierApplicationRow> = useMemo(
    () => [
      {
        title: 'Заявка',
        key: 'app',
        width: 200,
        render: (_: unknown, r: CarrierApplicationRow) => (
          <div onClick={(e) => e.stopPropagation()}>
            <ApplicationCell
              application={
                r.application ?? {
                  id: r.applicationId,
                  internalNumber: r.internalNumber,
                  applicationDate: r.applicationDate
                }
              }
            />
          </div>
        )
      },
      {
        title: 'Клиент',
        key: 'cl',
        width: 200,
        ellipsis: true,
        render: (_: unknown, r: CarrierApplicationRow) => {
          const a = r.application;
          if (!a?.clientId) return <Text type="secondary">{ENTITY_CARD_DASH}</Text>;
          return (
            <Link to={`/clients/${a.clientId}`} onClick={(e) => e.stopPropagation()}>
              {a.client?.name?.trim() || ENTITY_CARD_DASH}
            </Link>
          );
        }
      },
      {
        title: 'Внешний №',
        key: 'ext',
        width: 200,
        render: (_: unknown, r: CarrierApplicationRow) => {
          if (!r.application) return <Text type="secondary">{ENTITY_CARD_DASH}</Text>;
          return <ExternalApplicationCell application={r.application} />;
        }
      },
      {
        title: 'Погрузка',
        key: 'pl',
        width: 120,
        render: (_: unknown, r: CarrierApplicationRow) => (
          <span>{r.application ? formatDate(r.application.plannedLoadingDate) : ENTITY_CARD_DASH}</span>
        )
      },
      {
        title: 'Выгрузка',
        key: 'unl',
        width: 120,
        render: (_: unknown, r: CarrierApplicationRow) => (
          <span>{r.application ? formatDate(r.application.plannedUnloadingDate) : ENTITY_CARD_DASH}</span>
        )
      },
      {
        title: 'Статус',
        key: 'st',
        width: 130,
        render: (_: unknown, r: CarrierApplicationRow) =>
          r.application ? <StatusCell status={String(r.application.status)} /> : <Text type="secondary">{ENTITY_CARD_DASH}</Text>
      }
    ],
    []
  );

  const tnColumns: ColumnsType<CarrierTnRow> = useMemo(
    () => [
      {
        title: 'Документ',
        dataIndex: 'label',
        key: 'label',
        ellipsis: true
      },
      {
        title: 'Маршрут',
        key: 'route',
        width: 140,
        render: (_: unknown, r: CarrierTnRow) => (
          <Button type="link" size="small" onClick={() => navigate(`/routes/${r.routeId}`)}>
            №{r.routeNumber}
          </Button>
        )
      },
      {
        title: '',
        key: 'tn',
        width: 140,
        align: 'right',
        render: (_: unknown, r: CarrierTnRow) =>
          r.tnId != null ? (
            <Button type="link" size="small" onClick={() => navigate(`/tn/${r.tnId}/view`)}>
              Карточка ТН
            </Button>
          ) : (
            <Text type="secondary">нет ссылки</Text>
          )
      }
    ],
    [navigate]
  );

  const renderPenaltyDetails = (items: CarrierPenaltyTableRow[]) => {
    if (items.length === 0) {
      return (
        <div className="carrier-penalties-details carrier-penalties-details--empty">
          <Text type="secondary">нет штрафов по этому маршруту</Text>
        </div>
      );
    }
    return (
      <div className="carrier-penalties-details">
        {items.map((item) => (
          <div key={item.key} className="carrier-penalties-details-row">
            <Text className="carrier-penalties-col-reason" ellipsis={{ tooltip: item.reason }}>
              {item.reason}
            </Text>
            <Text className="carrier-penalties-col-amount">{formatRub(item.amount)}</Text>
            <span className="carrier-penalties-col-status">
              <Tag color={statusTagColor(item.status)} className="carrier-penalties-tag">
                {penaltyStatusLabel(item.status)}
              </Tag>
            </span>
          </div>
        ))}
      </div>
    );
  };

  const penaltiesTabContent = penaltiesError ? (
    <Alert type="warning" message={penaltiesError} showIcon />
  ) : penaltiesLoading ? (
    <div style={{ textAlign: 'center', padding: 32 }}>
      <Spin />
    </div>
  ) : routeRows.length === 0 ? (
    <Text type="secondary">{emptyListMessage('маршрутов у этого перевозчика')}</Text>
  ) : (
    <Table<CarrierRoutePenaltiesRow>
      className="carrier-penalties-routes-table table-card-rows entity-card-table"
      size="small"
      rowKey="key"
      columns={routeTableColumns}
      dataSource={routeRows}
      rowClassName={(record) =>
        record.penaltyCount > 0
          ? 'carrier-penalties-row-has-penalties'
          : 'carrier-penalties-row-no-penalties'
      }
      pagination={{ pageSize: 20, showSizeChanger: true, pageSizeOptions: ['10', '20', '50', '100'], showTotal: (t) => `Всего: ${t}` }}
      scroll={{ x: 'max-content' }}
      expandable={{
        expandedRowKeys,
        onExpandedRowsChange: setExpandedRowKeys,
        expandRowByClick: true,
        rowExpandable: (record) => record.penaltyCount > 0,
        expandIcon: ({ expanded, onExpand, record }) => {
          if (record.penaltyCount === 0) {
            return <span className="carrier-penalties-expand-placeholder" aria-hidden />;
          }
          return (
            <button
              type="button"
              className="carrier-penalties-expand-btn"
              aria-expanded={expanded}
              aria-label={expanded ? 'Свернуть штрафы' : 'Показать штрафы'}
              onClick={(e) => {
                e.stopPropagation();
                onExpand(record, e);
              }}
            >
              <span className="carrier-penalties-expand-glyph" aria-hidden>
                {expanded ? '▾' : '▸'}
              </span>
            </button>
          );
        },
        expandedRowRender: (record) => (
          <div className="carrier-penalties-nested">
            <div className="carrier-penalties-nested-inner">{renderPenaltyDetails(record.penalties)}</div>
          </div>
        )
      }}
      onRow={(record) => ({
        style: { cursor: record.penaltyCount > 0 ? 'pointer' : 'default' }
      })}
    />
  );

  const routesTabContent =
    penaltiesError ? (
      <Alert type="warning" message={penaltiesError} showIcon />
    ) : penaltiesLoading ? (
      <div style={{ textAlign: 'center', padding: 32 }}>
        <Spin />
      </div>
    ) : routeRows.length === 0 ? (
      <Text type="secondary">{emptyListMessage('маршрутов у этого перевозчика')}</Text>
    ) : (
      <Table<CarrierRoutePenaltiesRow>
        className="table-card-rows entity-card-table"
        size="small"
        rowKey="key"
        columns={routeTableColumns}
        dataSource={routeRows}
        pagination={{ pageSize: 20, showSizeChanger: true, pageSizeOptions: ['10', '20', '50', '100'], showTotal: (t) => `Всего: ${t}` }}
        scroll={{ x: 'max-content' }}
        onRow={(record) => ({
          onClick: () => navigate(`/routes/${record.routeId}`),
          style: { cursor: 'pointer' }
        })}
      />
    );

  const applicationsTabContent =
    penaltiesError ? (
      <Alert type="warning" message={penaltiesError} showIcon />
    ) : penaltiesLoading ? (
      <div style={{ textAlign: 'center', padding: 32 }}>
        <Spin />
      </div>
    ) : applicationRows.length === 0 ? (
      <Text type="secondary">{emptyListMessage('заявок по маршрутам этого перевозчика')}</Text>
    ) : (
      <Table<CarrierApplicationRow>
        className="table-card-rows entity-card-table"
        size="small"
        rowKey="key"
        columns={applicationColumns}
        dataSource={applicationRows}
        pagination={{ pageSize: 20, showTotal: (t) => `Всего: ${t}` }}
        onRow={(record) => ({
          onClick: () => navigate(`/applications/${record.applicationId}`),
          style: { cursor: 'pointer' }
        })}
      />
    );

  const tnTabContent =
    penaltiesError ? (
      <Alert type="warning" message={penaltiesError} showIcon />
    ) : penaltiesLoading ? (
      <div style={{ textAlign: 'center', padding: 32 }}>
        <Spin />
      </div>
    ) : tnRows.length === 0 ? (
      <Text type="secondary">{emptyListMessage('транспортных накладных по маршрутам')}</Text>
    ) : (
      <Table<CarrierTnRow>
        className="table-card-rows entity-card-table"
        size="small"
        rowKey="key"
        columns={tnColumns}
        dataSource={tnRows}
        pagination={{ pageSize: 20, showTotal: (t) => `Всего: ${t}` }}
        onRow={(record) => ({
          onClick: () => {
            if (record.tnId != null) navigate(`/tn/${record.tnId}/view`);
            else navigate(`/routes/${record.routeId}`);
          },
          style: { cursor: 'pointer' }
        })}
      />
    );

  if (!canViewReferences) {
    return (
      <PageLayout title="Перевозчик">
        <Card>
          <Text>У вас нет прав для просмотра справочников.</Text>
        </Card>
      </PageLayout>
    );
  }

  if (skip) {
    return (
      <PageLayout title="Перевозчик">
        <Alert type="error" message="Некорректная ссылка" showIcon />
      </PageLayout>
    );
  }

  if (isLoading) {
    return (
      <PageLayout title="Перевозчик" titleLevel={2}>
        <div style={{ textAlign: 'center', padding: 48 }}>
          <Spin size="large" />
        </div>
      </PageLayout>
    );
  }

  if (isError || !data) {
    const msg =
      (error as { data?: { error?: string } })?.data?.error ||
      'Не удалось загрузить перевозчика. Возможно, запись удалена или недоступна.';
    return (
      <PageLayout title="Перевозчик">
        <Alert type="error" message={msg} showIcon />
      </PageLayout>
    );
  }

  const c = data;

  const headerSubtitle = (
    <Space direction="vertical" size={4}>
      <Text type="secondary">{innSubtitleLine(c.inn)}</Text>
      {penaltiesError ? (
        <Text type="secondary">Штрафы: не удалось загрузить сводку</Text>
      ) : penaltiesLoading ? (
        <Text type="secondary">Штрафы: загрузка сводки…</Text>
      ) : (
        <Text type="secondary">
          Штрафы: {formatRub(totalRub)} ({penaltyCount === 0 ? 'нет начислений' : `${penaltyCount} шт.`}) · Маршрутов:{' '}
          {routesCount}
        </Text>
      )}
    </Space>
  );

  const cardTabContent = (
    <Space direction="vertical" size={16} style={{ width: '100%' }} className="entity-card-tab-body">
      <Card title={sectionTitle('Основная информация')} size="small" styles={{ body: { padding: '12px 14px' } }} style={cardSurface}>
        <Descriptions column={1} size="small" colon>
          <Descriptions.Item label="Наименование">{c.name?.trim() || EMPTY_FIELD}</Descriptions.Item>
          <Descriptions.Item label="Создан">
            {c.createdAt ? formatDateTime(c.createdAt) : 'Дата создания не заполнена'}
          </Descriptions.Item>
        </Descriptions>
      </Card>

      <Card title={sectionTitle('Контакты')} size="small" styles={{ body: { padding: '12px 14px' } }} style={cardSurface}>
        <Descriptions column={1} size="small" colon>
          <Descriptions.Item label="Контактное лицо">{c.contactPerson?.trim() || EMPTY_FIELD}</Descriptions.Item>
          <Descriptions.Item label="Телефон">{c.phone?.trim() || EMPTY_FIELD}</Descriptions.Item>
          <Descriptions.Item label="Email">{c.email?.trim() || EMPTY_FIELD}</Descriptions.Item>
        </Descriptions>
      </Card>

      <Card title={sectionTitle('Реквизиты')} size="small" styles={{ body: { padding: '12px 14px' } }} style={cardSurface}>
        <Descriptions column={1} size="small" colon>
          <Descriptions.Item label="ИНН">{c.inn?.trim() || EMPTY_FIELD}</Descriptions.Item>
          <Descriptions.Item label="КПП">{c.kpp?.trim() || EMPTY_FIELD}</Descriptions.Item>
          <Descriptions.Item label="ОГРН">{c.ogrn?.trim() || EMPTY_FIELD}</Descriptions.Item>
          <Descriptions.Item label="Юр. адрес">{c.legalAddress?.trim() || EMPTY_FIELD}</Descriptions.Item>
          <Descriptions.Item label="Почтовый адрес">{c.postalAddress?.trim() || EMPTY_FIELD}</Descriptions.Item>
          <Descriptions.Item label="Директор">{c.directorName?.trim() || EMPTY_FIELD}</Descriptions.Item>
        </Descriptions>
      </Card>

      <Card title={sectionTitle('Банк')} size="small" styles={{ body: { padding: '12px 14px' } }} style={cardSurface}>
        <Descriptions column={1} size="small" colon>
          <Descriptions.Item label="Банк">{c.bankName?.trim() || EMPTY_FIELD}</Descriptions.Item>
          <Descriptions.Item label="БИК">{c.bankBik?.trim() || EMPTY_FIELD}</Descriptions.Item>
          <Descriptions.Item label="Р/с">{c.bankAccount?.trim() || EMPTY_FIELD}</Descriptions.Item>
          <Descriptions.Item label="К/с">{c.bankCorrAccount?.trim() || EMPTY_FIELD}</Descriptions.Item>
        </Descriptions>
      </Card>
    </Space>
  );

  const tabItems = [
    { key: 'card', label: 'Карточка', children: cardTabContent },
    { key: 'penalties', label: 'Штрафы', children: penaltiesTabContent },
    { key: 'routes', label: `Маршруты (${routesCount})`, children: routesTabContent },
    { key: 'applications', label: `Заявки (${applicationRows.length})`, children: applicationsTabContent },
    ...(tnRows.length > 0
      ? [{ key: 'tn', label: `ТН (${tnRows.length})`, children: tnTabContent } as const]
      : [])
  ];

  return (
    <PageLayout
      titleLevel={2}
      title={c.name}
      subtitle={headerSubtitle}
      actions={
        isOverlay || canModifyReferences ? (
          <Space>
            {isOverlay && (
              <Button type="default" onClick={close}>
                Закрыть
              </Button>
            )}
            {canModifyReferences ? (
              <Button type="primary" icon={<EditOutlined />} onClick={() => setEditOpen(true)}>
                Редактировать
              </Button>
            ) : null}
          </Space>
        ) : undefined
      }
    >
      <Tabs defaultActiveKey="card" className="entity-card-tabs" items={tabItems} />

      {editOpen && (
        <CreateCarrierModal
          isOpen={editOpen}
          carrier={c}
          onClose={() => {
            setEditOpen(false);
            refetch();
          }}
          onCarrierCreated={() => {
            refetch();
          }}
        />
      )}
    </PageLayout>
  );
};

const CarrierCardPage: React.FC = () => (
  <EntityCardOverlayFrame>
    <CarrierCardPageContent />
  </EntityCardOverlayFrame>
);

export default CarrierCardPage;
